import { type HTMLAttributes, type ReactNode } from 'react';

import { CheckmarkCircleIc, DividerBulletOutlineIc } from '@dsch/dd-icons';

import { Panel } from '../Panel/Panel';
import { Accordion } from '../../../../../../components/ToolkitV2/Accordion/Accordion';

import { Header } from './CollapsiblePanel.styled';

import { theme } from '../../../../../../helpers/Theme';
import { Spacing } from '../../../../../../helpers/genericStyles';
import { useCollapsiblePanelContext } from './CollapsiblePanel.context';
import {
  usePanelState,
  usePanelDispatch,
} from '../../../../financeProviders/shared/PanelContext';
import { CollapsiblePanelNames } from '../../../../../../components/CarFinancePage/queries/schema/carFinance.schema';

const PANEL_STATUS = {
  COMPLETE: 'COMPLETE',
  INCOMPLETE: 'INCOMPLETE',
} as const;

type PanelStatus = (typeof PANEL_STATUS)[keyof typeof PANEL_STATUS];

type Props = HTMLAttributes<HTMLElement> &
  Spacing & {
    headerText: ReactNode;
    children: ReactNode;
    accordionName: string;
    panelName: CollapsiblePanelNames;
  };

const CollapsiblePanel = (props: Props) => {
  const {
    headerText,
    mb,
    children,
    accordionName,
    'aria-label': ariaLabel,
    panelName,
  } = props;
  const { status } = useCollapsiblePanelContext();
  const panelState = usePanelState();
  const dispatch = usePanelDispatch();

  const panelStatusIcon = (status: PanelStatus) => {
    return status === PANEL_STATUS.COMPLETE ? (
      <CheckmarkCircleIc
        color={theme.colors.GREEN}
        aria-live="polite"
        aria-label={`${accordionName} complete`}
        role="img"
      />
    ) : (
      <DividerBulletOutlineIc
        color={theme.colors.BLUE}
        aria-label={`${accordionName} incomplete`}
        role="img"
      />
    );
  };

  return (
    <Panel mb={mb} aria-label={ariaLabel}>
      <Accordion
        header={
          <Header>
            {panelStatusIcon(status)}
            {headerText}
          </Header>
        }
        content={children}
        showDivider={false}
        headerSpacerTop={null}
        headerSpacerBottom={null}
        bodySpacer={null}
        accordionName={accordionName}
        isOpen={panelState[panelName].isOpen}
        onChange={() => dispatch({ type: 'TOGGLE_PANEL', panel: panelName })}
      />
    </Panel>
  );
};

export { CollapsiblePanel, PANEL_STATUS, type PanelStatus };
